import { ArrowUpRight, CheckIcon, ClockIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import type { AssetWithStats } from '@/common/asset';
import { ROLE } from '@/common/role';
import { AssetIcon, InspectionIcon } from '@/components/entity-icons';
import { Restricted } from '@/components/Restricted';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn, formatCurrencyWithCents, formatDateOnly } from '@/lib/utils';
import { getConditionRatingColorsMapping } from '@/lib/utils/asset';
import { getMediaUrl } from '@/lib/utils/storage';
import { ImageWithFallback } from '../../imageWithFallback';
import { AssetInactiveBadge, AssetOrganizationLabel } from './AssetOrganizationLabel';
import { DamageStatBox } from './DamageStatBox';

/** @param showOrganization - label the card with the asset's organization (used when listing all organizations) */
export const AssetCard = ({
	asset,
	selected = false,
	showOrganization = false,
}: {
	asset: AssetWithStats;
	selected?: boolean;
	showOrganization?: boolean;
}) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const conditionRatingColorsMapping = getConditionRatingColorsMapping(asset.conditionRating || 0);

	const pendingCount = asset.financeStats?.Pending?.count || 0;
	const isPendingZero = pendingCount === 0;

	const assignedCount = asset.financeStats?.Assigned?.count || 0;
	const isAssignedZero = assignedCount === 0;

	const latestInspection = asset.latestInspection;
	const navigateToLatestInspection = (e: React.MouseEvent) => {
		// The whole card navigates to the asset; this button goes one level deeper.
		e.stopPropagation();
		if (latestInspection) navigate(`/assets/${asset.id}/inspections/${latestInspection.id}`);
	};

	return (
		<Card
			className={cn(
				'group h-full rounded-lg border border-border shadow-sm hover:shadow-lg transition-all duration-200 hover:-translate-y-1 flex flex-col overflow-hidden cursor-pointer w-full gap-0 bg-card p-0',
				selected && 'ring-2 ring-primary ring-offset-2 ring-offset-background',
			)}
			onClick={() => navigate(`/assets/${asset.id}`)}
		>
			{/* Top Image Section */}
			<div className="relative h-48 w-full overflow-hidden shrink-0 bg-muted/30">
				<ImageWithFallback
					src={getMediaUrl(asset.imageUrl)}
					alt={asset.name}
					className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
				/>

				{/* Ongoing Inspection Badge */}
				{asset.hasOngoingInspection && (
					<div className="absolute top-3 right-3 bg-primary text-primary-foreground px-2 py-1 rounded-md text-xs font-semibold shadow-md z-20">
						{t('assets.card.ongoingInspection')}
					</div>
				)}

				{/* Hover Overlay */}
				<div className="absolute inset-0 bg-black/60 flex flex-col items-center justify-center transition-opacity duration-200 opacity-0 group-hover:opacity-100 z-10">
					<ArrowUpRight className="text-white size-8 mb-1" />
					<span className="text-white text-sm font-semibold px-2 text-center">{t('assets.card.goToAsset')}</span>
				</div>
			</div>

			<CardHeader className="flex flex-col space-y-1.5 p-5 pb-3">
				<div className="flex justify-between items-start">
					<div className="flex-1 flex flex-col min-w-0">
						<div className="flex items-center gap-2 mb-1.5">
							<AssetIcon className="size-5 shrink-0 text-muted-foreground" aria-label={t('assets.card.iconLabel')} />
							<CardTitle className="text-xl text-card-foreground leading-tight line-clamp-1">{asset.name}</CardTitle>
							<span
								className={cn(
									'text-sm font-bold px-2 py-0.5 rounded shadow-sm border shrink-0',
									conditionRatingColorsMapping,
								)}
							>
								{(asset.conditionRating ?? 0).toFixed(1)}
							</span>
							<AssetInactiveBadge asset={asset} />
						</div>
						{showOrganization && <AssetOrganizationLabel asset={asset} className="mb-1" />}
						<CardDescription className="text-sm text-muted-foreground line-clamp-1">
							{asset.location || t('assets.card.noLocation')}
						</CardDescription>
						{/* Always one row high, so cards with and without inspections line up */}
						<Restricted to={ROLE.INSPECTIONS_READ}>
							<div className="mt-2 flex h-6 items-center">
								{latestInspection ? (
									<Tooltip>
										<TooltipTrigger asChild>
											<Button
												variant="outline"
												size="xs"
												className="bg-white cursor-pointer"
												onClick={navigateToLatestInspection}
											>
												<InspectionIcon /> {t('assets.list.lastInspection')}
												<span className="text-muted-foreground font-normal">
													{formatDateOnly(latestInspection.startDate)}
												</span>
											</Button>
										</TooltipTrigger>
										<TooltipContent>
											{t('assets.list.openLatestInspection', {
												title: `${latestInspection.title} (${formatDateOnly(latestInspection.startDate)})`,
											})}
										</TooltipContent>
									</Tooltip>
								) : (
									<span className="text-xs text-muted-foreground">{t('assets.list.noInspectionsYet')}</span>
								)}
							</div>
						</Restricted>
					</div>
				</div>
			</CardHeader>

			<CardContent className="flex-1 px-5 pb-5 pt-0">
				<Restricted to={ROLE.WORK_PACKAGES_READ}>
					<div className="grid grid-cols-2 gap-3 mb-4 mt-1">
						{/* Pending Work Packages */}
						<div className="flex flex-col p-3 bg-card rounded-lg border border-border shadow-sm relative overflow-hidden">
							<div
								className={cn(
									'absolute left-0 top-0 bottom-0 w-1',
									isPendingZero ? 'bg-muted-foreground/30' : 'bg-primary',
								)}
							/>
							<div className="pl-2 flex flex-col">
								<div className="flex justify-between items-center mb-1.5">
									<span className="text-[10px] text-muted-foreground font-bold uppercase tracking-wider flex items-center">
										<ClockIcon
											className={cn('h-3.5 w-3.5 mr-1', isPendingZero ? 'text-muted-foreground/50' : 'text-primary')}
										/>{' '}
										{t('assets.card.pendingWP')}
									</span>
								</div>
								<span
									className={cn(
										'text-base font-extrabold truncate',
										isPendingZero ? 'text-muted-foreground/50' : 'text-foreground',
									)}
								>
									{formatCurrencyWithCents(asset.financeStats?.Pending?.amount)}
								</span>
								<span
									className="text-[10px] uppercase font-semibold text-muted-foreground mt-1"
									title={t('assets.card.packageCount')}
								>
									{t('assets.list.packages', { count: pendingCount })}
								</span>
							</div>
						</div>

						{/* Assigned Work Packages */}
						<div className="flex flex-col p-3 bg-card rounded-lg border border-border shadow-sm relative overflow-hidden">
							<div
								className={cn(
									'absolute left-0 top-0 bottom-0 w-1',
									isAssignedZero ? 'bg-muted-foreground/30' : 'bg-emerald-500',
								)}
							/>
							<div className="pl-2 flex flex-col">
								<div className="flex justify-between items-center mb-1.5">
									<span className="text-[10px] text-muted-foreground font-bold uppercase tracking-wider flex items-center">
										<CheckIcon
											className={cn(
												'h-3.5 w-3.5 mr-1',
												isAssignedZero ? 'text-muted-foreground/50' : 'text-emerald-500',
											)}
										/>{' '}
										{t('assets.card.assignedWP')}
									</span>
								</div>
								<span
									className={cn(
										'text-base font-extrabold truncate',
										isAssignedZero ? 'text-muted-foreground/50' : 'text-foreground',
									)}
								>
									{formatCurrencyWithCents(asset.financeStats?.Assigned?.amount)}
								</span>
								<span
									className="text-[10px] uppercase font-semibold text-muted-foreground mt-1"
									title={t('assets.card.packageCount')}
								>
									{t('assets.list.packages', { count: assignedCount })}
								</span>
							</div>
						</div>
					</div>
				</Restricted>

				<Restricted to={ROLE.DAMAGES_READ}>
					<div>
						<span className="text-xs text-muted-foreground font-medium flex items-center mb-2">
							{t('assets.card.activeDamages')}
						</span>
						<div className="grid grid-cols-4 gap-2 text-center">
							<DamageStatBox
								label={t('assets.card.severity.crit')}
								count={asset.damageStats?.crit || 0}
								colorType="crit"
							/>
							<DamageStatBox
								label={t('assets.card.severity.high')}
								count={asset.damageStats?.high || 0}
								colorType="high"
							/>
							<DamageStatBox
								label={t('assets.card.severity.med')}
								count={asset.damageStats?.med || 0}
								colorType="med"
							/>
							<DamageStatBox
								label={t('assets.card.severity.low')}
								count={asset.damageStats?.low || 0}
								colorType="low"
							/>
						</div>
					</div>
				</Restricted>
			</CardContent>
		</Card>
	);
};
